import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { RATING_VALUES } from '@/lib/testimonials';

/**
 * ONE star component for the whole feature.
 *
 * - `value` + no `onChange`  -> read-only display (used by cards and admin).
 * - `value` + `onChange`     -> accessible radio group (used by the form).
 *
 * Deliberately NOT an emoji: lucide's <Star> keeps the same metrics in both
 * languages and can be half-coloured, sized and given an accessible label.
 */
interface Props {
  value: number;
  onChange?: (v: number) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-8 w-8' };

const StarRating: React.FC<Props> = ({ value, onChange, size = 'md', className = '' }) => {
  const { t } = useI18n();
  const [hover, setHover] = useState(0);
  const readOnly = !onChange;
  const shown = hover || value;
  const box = SIZES[size];

  if (readOnly) {
    return (
      <span
        className={`inline-flex items-center gap-0.5 ${className}`}
        role="img"
        aria-label={t('testi.ofFive', { rating: value })}
      >
        {RATING_VALUES.map((n) => (
          <Star
            key={n}
            className={`${box} ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
            aria-hidden="true"
          />
        ))}
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1 ${className}`}
      role="radiogroup"
      aria-label={t('testi.rating')}
      onMouseLeave={() => setHover(0)}
    >
      {RATING_VALUES.map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={t('testi.ofFive', { rating: n })}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onFocus={() => setHover(n)}
          onBlur={() => setHover(0)}
          className="rounded-md p-0.5 transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
        >
          <Star
            className={`${box} ${n <= shown ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
            aria-hidden="true"
          />
        </button>
      ))}
    </div>
  );
};

export default StarRating;
